import { appVersionOf } from "../shared/logging/clientLog";

/**
 * Settings of the OpenID Connect client (authorization code flow with PKCE).
 */
export interface OidcSettings {
  /** Issuer URL of the identity provider. */
  readonly authority: string;
  /** Public client identifier registered at the identity provider. */
  readonly clientId: string;
  /** Space-separated scopes requested at login. */
  readonly scope: string;
}

/**
 * Runtime configuration of the application, read once in the composition root.
 */
export interface AppConfig {
  /** Base URL of the API without trailing slash; empty for the same origin. */
  readonly apiBaseUrl: string;
  /** OpenID Connect settings. */
  readonly oidc: OidcSettings;
  /**
   * Whether the native apps of this build are set up for push notifications (FCM configuration
   * file and APNs entitlement present). Without it the apps never call the push plugin, which
   * would stop the Android app without Firebase configuration (ADR-0036).
   */
  readonly nativePush: boolean;
  /**
   * Absolute `https:` address of the operator's product website without trailing slash; the
   * native apps link the app variants of its legal pages (ADR-0087 decision 2).
   */
  readonly websiteUrl: string;
  /**
   * Version of the build in the reports to the operator's log (F-68): `VITE_APP_VERSION`, for
   * example the release or the commit; `dev` without one or with an invalid value.
   */
  readonly appVersion: string;
}

/** Environment variables the configuration is read from (Vite `import.meta.env`). */
export type ConfigSource = Readonly<Record<string, unknown>>;

const defaultAuthority = "http://localhost:8080/realms/timetracking";
const defaultClientId = "timetracking-pwa";
const defaultScope = "openid profile organization";
// [ORG] Placeholder of the operator's domain, as in the website drafts (ADR-0086); every release
// build of the native apps sets VITE_WEBSITE_URL.
const defaultWebsiteUrl = "https://example-operator.de";

/**
 * Reads an optional string variable and falls back to a default value.
 *
 * @param source - The environment variables.
 * @param name - The variable name.
 * @param fallback - The value used when the variable is missing or empty.
 * @returns The trimmed value or the fallback.
 */
function readString(source: ConfigSource, name: string, fallback: string): string {
  const raw = source[name];
  const value = typeof raw === "string" && raw.trim() !== "" ? raw.trim() : fallback;

  return value;
}

/**
 * Removes trailing slashes so that paths can be appended with a leading slash.
 *
 * @param url - The URL.
 * @returns The URL without trailing slashes.
 */
function trimTrailingSlashes(url: string): string {
  const trimmed = url.replace(/\/+$/u, "");

  return trimmed;
}

/**
 * Reads and validates the application configuration.
 *
 * @param source - The environment variables (`VITE_API_BASE_URL`, `VITE_OIDC_AUTHORITY`,
 *   `VITE_OIDC_CLIENT_ID`, `VITE_OIDC_SCOPE`, `VITE_NATIVE_PUSH`, `VITE_WEBSITE_URL`,
 *   `VITE_APP_VERSION`).
 * @returns The validated configuration.
 * @throws Error if a URL is malformed; the application cannot start without valid settings.
 */
export function readAppConfig(source: ConfigSource): AppConfig {
  const apiBaseUrl = trimTrailingSlashes(readString(source, "VITE_API_BASE_URL", ""));
  const authority = trimTrailingSlashes(
    readString(source, "VITE_OIDC_AUTHORITY", defaultAuthority),
  );

  if (!URL.canParse(authority)) {
    throw new Error(`VITE_OIDC_AUTHORITY is not a valid URL: '${authority}'.`);
  }

  if (apiBaseUrl !== "" && !URL.canParse(apiBaseUrl)) {
    throw new Error(`VITE_API_BASE_URL is not a valid URL: '${apiBaseUrl}'.`);
  }

  const websiteUrl = trimTrailingSlashes(readString(source, "VITE_WEBSITE_URL", defaultWebsiteUrl));

  // A site path does not resolve inside the native apps (ADR-0087 decision 2); paths of the legal
  // pages are appended, so neither query nor fragment is allowed.
  const website = URL.canParse(websiteUrl) ? new URL(websiteUrl) : null;

  if (website?.protocol !== "https:" || website.search !== "" || website.hash !== "") {
    throw new Error(
      `VITE_WEBSITE_URL must be an absolute https URL without query or fragment: '${websiteUrl}'.`,
    );
  }

  const config: AppConfig = {
    apiBaseUrl,
    oidc: {
      authority,
      clientId: readString(source, "VITE_OIDC_CLIENT_ID", defaultClientId),
      scope: readString(source, "VITE_OIDC_SCOPE", defaultScope),
    },
    nativePush: readString(source, "VITE_NATIVE_PUSH", "false") === "true",
    websiteUrl,
    appVersion: appVersionOf(readString(source, "VITE_APP_VERSION", "dev")),
  };

  return config;
}
